import { Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { PanelLayout } from '@/app/layout/PanelLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { Spinner } from '@/shared/components/Spinner';
import { lazyWithReload as lazy } from '@/shared/lib/lazy-with-reload';
import { NotFoundPage } from '@/app/pages/NotFoundPage';
import { AccessDeniedPage } from '@/app/pages/AccessDeniedPage';
import { SubscriptionBlockedPage } from '@/app/pages/SubscriptionBlockedPage';
import { StoreDeactivatedPage } from '@/app/pages/StoreDeactivatedPage';

/* eslint-disable react-refresh/only-export-components -- route-table module, not a hot-reload boundary */

/* ---- Lazy-loaded page chunks ---- */

const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('@/features/auth/pages/VerifyEmailPage'));
const TenantSelectPage = lazy(() => import('@/features/auth/pages/TenantSelectPage'));
const InvitePage = lazy(() => import('@/features/auth/pages/InvitePage'));

const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const CatalogPage = lazy(() => import('@/features/catalog/pages/CatalogPage'));
const MenuItemEditorPage = lazy(
  () => import('@/features/catalog/pages/MenuItemEditorPage'),
);
const PromotionsPage = lazy(() => import('@/features/promotions/pages/PromotionsPage'));
const PromotionEditorPage = lazy(() => import('@/features/promotions/pages/PromotionEditorPage'));
const PromotionDetailPage = lazy(() => import('@/features/promotions/pages/PromotionDetailPage'));
const OrdersPage = lazy(() => import('@/features/orders/pages/OrdersPage'));
const QrCodePage = lazy(() => import('@/features/qrcode/pages/QrCodePage'));
const OrderHistoryPage = lazy(() => import('@/features/orders/pages/OrderHistoryPage'));
const ConversationsPage = lazy(() => import('@/features/conversations/pages/ConversationsPage'));
const SalesReportPage = lazy(() => import('@/features/reports/pages/SalesReportPage'));
const SettingsLayout = lazy(() => import('@/features/settings/pages/SettingsLayout'));
const ProfilePage = lazy(() => import('@/features/settings/pages/ProfilePage'));
const StoreSettingsPage = lazy(() => import('@/features/settings/pages/StoreSettingsPage'));
const DeliverySettingsPage = lazy(() => import('@/features/settings/pages/DeliverySettingsPage'));
const PaymentMethodsPage = lazy(() => import('@/features/settings/pages/PaymentMethodsPage'));
const PrintingSettingsPage = lazy(() => import('@/features/printing/pages/PrintingSettingsPage'));
const PrintingSetupPage = lazy(() => import('@/features/printing/pages/PrintingSetupPage'));
const PrintingPairPage = lazy(() => import('@/features/printing/pages/PrintingPairPage'));
const CustomerAgentPage = lazy(() => import('@/features/customer-agent/pages/CustomerAgentPage'));
const WhatsappConnectPage = lazy(() => import('@/features/whatsapp/pages/WhatsappConnectPage'));
const BillingPage = lazy(() => import('@/features/billing/pages/BillingPage'));
const TeamPage = lazy(() => import('@/features/team/pages/TeamPage'));
const ChangePasswordPage = lazy(() => import('@/features/auth/pages/ChangePasswordPage'));
const SessionsPage = lazy(() => import('@/features/auth/pages/SessionsPage'));

/* ---- Suspense wrapper ---- */

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Spinner fullPage size="lg" />}>{children}</Suspense>;
}

/* ---- Router definition ---- */

export const router = createBrowserRouter([
  // Public routes
  {
    path: '/login',
    element: <LazyPage><LoginPage /></LazyPage>,
  },
  {
    path: '/register',
    element: <LazyPage><RegisterPage /></LazyPage>,
  },
  {
    path: '/forgot-password',
    element: <LazyPage><ForgotPasswordPage /></LazyPage>,
  },
  {
    path: '/reset-password',
    element: <LazyPage><ResetPasswordPage /></LazyPage>,
  },
  {
    path: '/verify-email',
    element: <LazyPage><VerifyEmailPage /></LazyPage>,
  },
  {
    path: '/select-tenant',
    element: <LazyPage><TenantSelectPage /></LazyPage>,
  },
  {
    path: '/invite/:token',
    element: <LazyPage><InvitePage /></LazyPage>,
  },

  // Protected panel routes
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <PanelLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      {
        path: 'dashboard',
        element: <LazyPage><DashboardPage /></LazyPage>,
      },
      {
        path: 'catalog',
        element: (
          <ProtectedRoute requiredPermissions={['catalog.read']}>
            <LazyPage><CatalogPage /></LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'catalog/items/new',
        element: (
          <ProtectedRoute requiredPermissions={['catalog.read']}>
            <LazyPage><MenuItemEditorPage /></LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'catalog/items/:productId',
        element: (
          <ProtectedRoute requiredPermissions={['catalog.read']}>
            <LazyPage><MenuItemEditorPage /></LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        // Promoção mexe no preço do cardápio: lê quem lê o catálogo, e
        // cria, edita e pausa quem escreve nele.
        path: 'promotions',
        element: (
          <ProtectedRoute requiredPermissions={['catalog.read']}>
            <LazyPage><PromotionsPage /></LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'promotions/new',
        element: (
          <ProtectedRoute requiredPermissions={['catalog.write']}>
            <LazyPage><PromotionEditorPage /></LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'promotions/:promotionId',
        element: (
          <ProtectedRoute requiredPermissions={['catalog.read']}>
            <LazyPage><PromotionDetailPage /></LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'promotions/:promotionId/edit',
        element: (
          <ProtectedRoute requiredPermissions={['catalog.write']}>
            <LazyPage><PromotionEditorPage /></LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'orders',
        element: (
          <ProtectedRoute requiredPermissions={['orders.read']}>
            <LazyPage><OrdersPage /></LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'orders/history',
        element: (
          <ProtectedRoute requiredPermissions={['orders.read']}>
            <LazyPage><OrderHistoryPage /></LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        // Atendimento (change customer-agent-handoff): `conversations.read`
        // pela rota; a página confere a área do Atendente IA (flag, direito
        // do plano e 404 lembrado) e mostra "não encontrado" sem ela.
        path: 'conversations',
        element: (
          <ProtectedRoute requiredPermissions={['conversations.read']}>
            <LazyPage><ConversationsPage /></LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'conversations/:conversationId',
        element: (
          <ProtectedRoute requiredPermissions={['conversations.read']}>
            <LazyPage><ConversationsPage /></LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        // Sem `requiredPermissions`: os dois GETs que a pagina consome nao
        // exigem permissao especifica na API, e reimprimir o cartaz que caiu
        // da parede nao e tarefa restrita.
        path: 'qr-code',
        element: <LazyPage><QrCodePage /></LazyPage>,
      },
      {
        path: 'reports',
        element: (
          <ProtectedRoute requiredPermissions={['reports.read']}>
            <LazyPage><SalesReportPage /></LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute requiredPermissions={['settings.read']}>
            <LazyPage><SettingsLayout /></LazyPage>
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="/settings/profile" replace /> },
          {
            path: 'profile',
            element: <LazyPage><ProfilePage /></LazyPage>,
          },
          {
            path: 'store',
            element: <LazyPage><StoreSettingsPage /></LazyPage>,
          },
          {
            path: 'delivery',
            element: <LazyPage><DeliverySettingsPage /></LazyPage>,
          },
          {
            path: 'payment-methods',
            element: <LazyPage><PaymentMethodsPage /></LazyPage>,
          },
          {
            // A página some fora do piloto (flag `panel.printing` ou 404 da
            // API) e fica só de leitura sem `settings.write`.
            path: 'printing',
            element: <LazyPage><PrintingSettingsPage /></LazyPage>,
          },
          {
            path: 'printing/setup',
            element: (
              <ProtectedRoute requiredPermissions={['settings.write']}>
                <LazyPage><PrintingSetupPage /></LazyPage>
              </ProtectedRoute>
            ),
          },
          {
            // Atendente IA (change whatsapp-channel): a página volta para
            // Configurações sem a flag, o direito do plano ou com o 404
            // `whatsapp.disabled`. As seções das outras changes entram nela.
            path: 'customer-agent',
            element: <LazyPage><CustomerAgentPage /></LazyPage>,
          },
          {
            // Sem `ProtectedRoute` de `whatsapp.manage`: o dono com o token
            // anterior ao deploy cairia na página genérica de acesso negado.
            // A página diz que só o dono conecta, sem chamar a API de sessão.
            path: 'customer-agent/connect',
            element: <LazyPage><WhatsappConnectPage /></LazyPage>,
          },
        ],
      },
      {
        // Aberta pelo instalador do computador da loja. Fora do layout de
        // Configurações (que exige `settings.read`), para a Cozinha que abrir
        // o link ler que só o dono ou o gerente pareia, em vez de cair no 403.
        // Sem sessão, o ProtectedRoute do painel leva ao login e volta aqui.
        path: 'settings/printing/pair',
        element: <LazyPage><PrintingPairPage /></LazyPage>,
      },
      {
        path: 'billing',
        element: (
          <ProtectedRoute requiredPermissions={['billing.read']}>
            <LazyPage><BillingPage /></LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'team',
        element: (
          <ProtectedRoute requiredPermissions={['team.manage']}>
            <LazyPage><TeamPage /></LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: 'change-password',
        element: <LazyPage><ChangePasswordPage /></LazyPage>,
      },
      {
        path: 'sessions',
        element: <LazyPage><SessionsPage /></LazyPage>,
      },
    ],
  },

  // Error pages
  { path: '/subscription-blocked', element: <SubscriptionBlockedPage /> },
  { path: '/store-deactivated', element: <StoreDeactivatedPage /> },
  { path: '/403', element: <AccessDeniedPage /> },
  { path: '*', element: <NotFoundPage /> },
]);
